import { Entity } from "../Types";

function getAttackDiceMap(): Map<number, string> {
  const attackDiceMap = new Map();
  attackDiceMap.set(3, "d4");
  attackDiceMap.set(4, "d6");
  attackDiceMap.set(5, "d8");
  attackDiceMap.set(6, "d10");
  attackDiceMap.set(7, "d4+d6");
  attackDiceMap.set(8, "d4+d8");
  attackDiceMap.set(9, "d6+d8");
  attackDiceMap.set(10, "2d8");
  attackDiceMap.set(11, "d8+d10");
  attackDiceMap.set(12, "2d4+d10");
  attackDiceMap.set(13, "d10+d12");
  attackDiceMap.set(14, "2d12");
  attackDiceMap.set(15, "d6+d20");
  attackDiceMap.set(16, "2d8+d10");
  attackDiceMap.set(17, "d4+2d12");
  attackDiceMap.set(18, "3d10");
  attackDiceMap.set(19, "2d6+d20");
  attackDiceMap.set(20, "d10+2d12");
  attackDiceMap.set(21, "3d12");
  attackDiceMap.set(22, "2d20");
  return attackDiceMap;
}

export const attackDiceMap = getAttackDiceMap();

function getAttackDiceLabel(value: number) {
  return { value: value, label: attackDiceMap.get(value) };
}

export const ATTACK_DICE = {
  minion: [getAttackDiceLabel(3), getAttackDiceLabel(4), getAttackDiceLabel(5), getAttackDiceLabel(6)],
  standard: [
    getAttackDiceLabel(7),
    getAttackDiceLabel(8),
    getAttackDiceLabel(9),
    getAttackDiceLabel(10),
    getAttackDiceLabel(11),
    getAttackDiceLabel(12),
  ],
  elite: [
    getAttackDiceLabel(8),
    getAttackDiceLabel(9),
    getAttackDiceLabel(10),
    getAttackDiceLabel(11),
    getAttackDiceLabel(12),
    getAttackDiceLabel(13),
  ],
  boss: [
    getAttackDiceLabel(14),
    getAttackDiceLabel(15),
    getAttackDiceLabel(16),
    getAttackDiceLabel(17),
    getAttackDiceLabel(18),
    getAttackDiceLabel(19),
  ],
  mega_boss: [
    getAttackDiceLabel(17),
    getAttackDiceLabel(18),
    getAttackDiceLabel(19),
    getAttackDiceLabel(20),
    getAttackDiceLabel(21),
    getAttackDiceLabel(22),
  ],
};

export type AttackDice = typeof ATTACK_DICE.minion;

export enum PowerLevel {
  Minion = "Minion",
  Standard = "Standard",
  Elite = "Elite",
  Boss = "Boss",
  MegaBoss = "Mega Boss",
}

function setMonsterStat(min: number, max: number, _default: number) {
  return {
    min: min,
    max: max,
    default: _default,
  };
}

function setMonsterAttackDice(min: number, max: number, _default: number, attackDice: AttackDice) {
  return {
    min: min,
    max: max,
    default: _default,
    marks: attackDice,
  };
}

const minionStats = {
  ability: setMonsterStat(-2, 5, 2),
  health: setMonsterStat(7, 12, 10),
  armor: setMonsterStat(0, 8, 2),
  speed: setMonsterStat(0, 50, 25),
  attackDice: setMonsterAttackDice(3, 6, 4, ATTACK_DICE.minion),
  attackBonus: setMonsterStat(2, 5, 3),
  attackRadius: setMonsterStat(0, 5, 0),
  attackRange: setMonsterStat(5, 30, 5),
  actions: setMonsterStat(1, 1, 1),
  reactions: setMonsterStat(0, 0, 0),
};

const standardStat = {
  ability: setMonsterStat(-1, 7, 3),
  health: setMonsterStat(35, 60, 45),
  armor: setMonsterStat(2, 10, 6),
  speed: setMonsterStat(0, 50, 30),
  attackDice: setMonsterAttackDice(7, 12, 10, ATTACK_DICE.standard),
  attackBonus: setMonsterStat(3, 6, 4),
  attackRadius: setMonsterStat(0, 5, 0),
  attackRange: setMonsterStat(5, 45, 5),
  actions: setMonsterStat(1, 2, 1),
  reactions: setMonsterStat(0, 1, 0),
};

const eliteStat = {
  ability: setMonsterStat(0, 8, 4),
  health: setMonsterStat(55, 100, 75),
  armor: setMonsterStat(6, 15, 11),
  speed: setMonsterStat(0, 50, 30),
  attackDice: setMonsterAttackDice(8, 13, 10, ATTACK_DICE.elite),
  attackBonus: setMonsterStat(5, 10, 8),
  attackRadius: setMonsterStat(0, 10, 0),
  attackRange: setMonsterStat(5, 60, 5),
  actions: setMonsterStat(1, 3, 2),
  reactions: setMonsterStat(0, 2, 1),
};

const bossStat = {
  ability: setMonsterStat(2, 11, 6),
  health: setMonsterStat(130, 230, 170),
  armor: setMonsterStat(8, 20, 15),
  speed: setMonsterStat(0, 55, 30),
  attackDice: setMonsterAttackDice(14, 19, 16, ATTACK_DICE.boss),
  attackBonus: setMonsterStat(8, 15, 10),
  attackRadius: setMonsterStat(0, 15, 0),
  attackRange: setMonsterStat(5, 60, 5),
  actions: setMonsterStat(1, 3, 2),
  reactions: setMonsterStat(0, 3, 2),
};

const megaBossStat = {
  ability: setMonsterStat(3, 13, 7),
  health: setMonsterStat(300, 600, 400),
  armor: setMonsterStat(10, 30, 20),
  speed: setMonsterStat(0, 60, 35),
  attackDice: setMonsterAttackDice(17, 22, 20, ATTACK_DICE.mega_boss),
  attackBonus: setMonsterStat(11, 15, 13),
  attackRadius: setMonsterStat(0, 30, 5),
  attackRange: setMonsterStat(5, 70, 5),
  actions: setMonsterStat(2, 4, 3),
  reactions: setMonsterStat(0, 5, 3),
};

export type MonsterStats = typeof minionStats;

export function getMonsterStatMap(): Map<PowerLevel, MonsterStats> {
  const monsterStatMap = new Map();
  monsterStatMap.set(PowerLevel.Minion, minionStats);
  monsterStatMap.set(PowerLevel.Standard, standardStat);
  monsterStatMap.set(PowerLevel.Elite, eliteStat);
  monsterStatMap.set(PowerLevel.Boss, bossStat);
  monsterStatMap.set(PowerLevel.MegaBoss, megaBossStat);
  return monsterStatMap;
}

export interface MonsterEntity extends Entity {
  motivation: string;
  powerLevel: PowerLevel;
  str: number;
  dex: number;
  int: number;
  wis: number;
  cha: number;
  health: number;
  armor: number;
  speed: number;
  attackDice: number;
  attackBonus: number;
  attackRange: number;
  attackRadius: number;
  attackTarget: string;
  attackModifier1: string;
  attackModifier2: string;
  attackModifier3: string;
  attackModifierUses1: number;
  attackModifierUses2: number;
  attackModifierUses3: number;
  reactions: number;
  reactionDescription1: string;
  reactionDescription2: string;
  reactionDescription3: string;
  reactionDescription4: string;
  reactionDescription5: string;
  reactionUses1: number;
  reactionUses2: number;
  reactionUses3: number;
  reactionUses4: number;
  reactionUses5: number;
  damageType: string;
  actions: number;
  description: string;
  background: string;
  strengths: string[];
  weaknesses: string[];
  tags: string[];
  specialNotes: string;
}

const defaultMonsterStats = getMonsterStatMap().get(PowerLevel.Minion)!;
export const DEFAULT_REACTION = "Oppurtunity Attack";
export const DEFAULT_MEGA_REACTION = "Oppurtunity Attack reduces target speed to 0";

export const defaultMonsterEntity: MonsterEntity = {
  name: "",
  motivation: "",
  powerLevel: PowerLevel.Minion,
  description: "",
  background: "",
  str: defaultMonsterStats.ability.default,
  dex: defaultMonsterStats.ability.default,
  int: defaultMonsterStats.ability.default,
  wis: defaultMonsterStats.ability.default,
  cha: defaultMonsterStats.ability.default,
  health: defaultMonsterStats.health.default,
  armor: defaultMonsterStats.armor.default,
  speed: 30,
  actions: defaultMonsterStats.actions.default,
  attackDice: defaultMonsterStats.attackDice.default,
  attackBonus: defaultMonsterStats.attackBonus.default,
  attackRange: 5,
  attackRadius: 0,
  attackTarget: "Armor",
  attackModifier1: "",
  attackModifier2: "",
  attackModifier3: "",
  attackModifierUses1: 2,
  attackModifierUses2: 2,
  attackModifierUses3: 2,
  damageType: "Weapon",
  reactions: 0,
  reactionDescription1: DEFAULT_REACTION,
  reactionDescription2: "",
  reactionDescription3: "",
  reactionDescription4: "",
  reactionDescription5: "",
  reactionUses1: 2,
  reactionUses2: 2,
  reactionUses3: 2,
  reactionUses4: 2,
  reactionUses5: 2,
  strengths: [],
  weaknesses: [],
  tags: [],
  specialNotes: "",
  previewS3Key: "",
  fileS3Key: "",
  collected: false,
  isPublished: false,
  isVisible: true,
  collections: 0,
};

export enum AttackTargets {
  normal = "Normal",
  strength = "Spell vs Strength",
  agility = "Spell vs Agility",
  wisdom = "Spell vs Wisdom",
  intellect = "Spell vs Intellect",
  charisma = "Spell vs Charisma",
}

export const monsterTags = [
  "Beast",
  "Construct",
  "Criminal",
  "Dragonoid",
  "Eldritch",
  "Elemental",
  "Humanoid",
  "Tribal",
  "Tyranical",
  "Undead",
  "Unholy",
  "Worshipper",
  // Environments
  "Artic",
  "City",
  "Desert",
  "Jungle",
  "Mountains",
  "Swamp",
  "Underground",
  "Volcanic",
  "Water",
  // Type
  "Bruiser",
  "Leader",
  "Ranged",
  "Spellcaster",
  "Support",
  "Tank",
];
export const damageTypes = ["Weapon", "Acid", "Force", "Poison", "Fire", "Ice", "Lightning", "Psychic", "Sonic", "Radiant", "Necrotic"];
export const conditions = [
  "Berserk",
  "Blinded",
  "Charmed",
  "Decaying",
  "Enraged",
  "Exhausted",
  "Frightened",
  "Invisible",
  "Prone",
  "Restrained",
  "Stunned",
  "Surprised",
];

export const WEAK_MODIFICATIONS = [
  "",
  "Knock an enemy prone, -4 attack",
  "Add 5ft radius to attack, -5 attack",
  "Restrain an enemy, -7 attack",
  "Berserk an enemy, -10 attack",
  "Poison enemies for 5 dmg at end of their turn, attack deals half damage",
  "Requires 2 actions, +6 attack",
  "Charge, moving 30ft in a straight line, +5 attack",
  "Sacrifice half your health, next attack deals double damage",
  "Attack instead heals one ally",
  "Attack type changes to a Spell vs Wisdom",
  "Attack type changes to a Spell vs Intellect",
  "Attack type changes to a Spell vs Charisma",
  "Damage type changes to Fire, Lightning or Ice",
  "Damage type changes to Poison or Acid damage",
];

export const STRONG_MODIFICATIONS = [
  "",
  "Blind all enemies hit, -6 attack",
  "Add 10ft radius to attack, -8 attack",
  "Restrain all enemies hit, -8 attack",
  "Berserk all enemies hit, -12 attack",
  "Poison enemies for 10 dmg at start of their turn, attack deals half damage",
  "Requires 2 actions, +10 attack",
  "Charge, moving 30ft in a straight line, +8 attack",
  "Sacrifice half your health, attacks deal double damage",
  "Attack instead heals all allies it hits",
  "Any 1s makes attack deal 0 damage, reroll any number of criticals",
  "Attack type changes to a Spell vs Wisdom",
  "Attack type changes to a Spell vs Intellect",
  "Attack type changes to a Spell vs Charisma",
  "Damage type changes to any damage type",
];

export const WEAK_REACTIONS = [
  "",
  DEFAULT_REACTION,
  "Deal 5 direct damage to everyone within 5ft",
  "Attack an enemy after they attack you",
  "Double armor against an attack",
  "Double magic defense against a spell",
  "Switch places with an ally",
  "Heal 1d10 health",
  "Charisma contest vs spellcaster to cancel a spell",
  "Intellect contest vs spellcaster to redirect their non-damage spell",
];

export const STRONG_REACTIONS = [
  "",
  DEFAULT_MEGA_REACTION,
  "Deal 10 direct damage in a 5ft radius",
  "Attack an enemy just before they attack you",
  "Double armor until the start of the next enemy turn",
  "Double a stat until the end of your turn",
  "Teleport up to 30ft",
  "Heal 2d20 health",
  "Wisdom contest vs spellcaster to redirect any spell",
  "Wisdom, Intellect or Charisma vs spellcaster to have a spell hit and damage them instead",
  "Charisma contest to charm someone within 30ft",
];
